import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Box, Icon, ROW_RUNGS, Text, type IconName } from "@salestrak/ui";

import { useShowEmptyProperties } from "#app/lib/hooks/use-show-empty-properties";

type SummaryEmptyFold = {
  /** True while the rows that hold no value are tucked away. */
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
};

const SummaryEmptyFoldContext = createContext<SummaryEmptyFold | null>(null);

function useEmptyFoldState(): SummaryEmptyFold {
  const showEmpty = useShowEmptyProperties();
  // The preference seeds each open; "More" and the collapse row then act
  // on this open alone. The provider remounts per entity (`key`), so a new
  // entity re-reads the preference rather than inheriting the last press.
  const [collapsed, setCollapsedState] = useState(!showEmpty);
  const setCollapsed = useCallback(
    (next: boolean) => setCollapsedState(next),
    [],
  );
  // One stable object per fold state, so a host effect can list it as a
  // dependency without re-running on every render.
  return useMemo(() => ({ collapsed, setCollapsed }), [collapsed, setCollapsed]);
}

/**
 * The ONE "hide what is empty" state for a summary: folding empties in the
 * property rows also folds the empty Fields, and either section's "More" row
 * unfolds both.
 *
 * A property with no value is not information, so the fold starts CLOSED:
 * a fresh Project reads Type · Workflow · Stage · Assignees · Address and
 * one "More" row, and a row appears the moment it holds a value (Asana's
 * grammar). "More" is a reveal for THIS open, not a preference — mount the
 * provider with `key={entityId}` so the next entity opens folded again. A
 * viewer who revealed the empties once to fill one in is not asking to see
 * every empty row on every entity from then on, which is what the earlier
 * persisted toggle did. A viewer who DOES want that says so once: the
 * `showEmptyProperties` preference (Preferences › Interface, "Show empty
 * properties") seeds every open unfolded, and "More" still folds this one.
 *
 * A fold never empties a section: each section applies it only while at
 * least one valued (or pinned) row stays visible, and offers no collapse row
 * otherwise. A header over a lone "More" row is exactly the UI this rule
 * exists to prevent. The create flow never folds — every row is there to be
 * filled.
 */
export function SummaryEmptyFoldProvider({ children }: { children: ReactNode }) {
  const value = useEmptyFoldState();
  return (
    <SummaryEmptyFoldContext.Provider value={value}>
      {children}
    </SummaryEmptyFoldContext.Provider>
  );
}

/** The summary's fold; a section mounted with no provider folds on its own. */
export function useSummaryEmptyFold(): SummaryEmptyFold {
  const shared = useContext(SummaryEmptyFoldContext);
  const local = useEmptyFoldState();
  return shared ?? local;
}

/**
 * Project, Account, and Contact properties share one fold control. Keep both
 * states on the property rows' pitch (ROW_RUNGS.propertyRow, 32) so the
 * property gap and divider gap stay consistent.
 * Render nothing when no empty property can fold, including create drafts.
 */
export function SummaryPropertiesFoldRow({
  fold,
  foldable,
}: {
  fold: SummaryEmptyFold;
  foldable: boolean;
}) {
  if (!foldable) return null;
  return (
    <Box height={ROW_RUNGS.propertyRow} justify="center" alignSelf="start">
      <SummaryFoldRow
        icon={fold.collapsed ? "caret-right" : "caret-up"}
        label={fold.collapsed ? "More" : "Less"}
        expanded={!fold.collapsed}
        onClick={() => fold.setCollapsed(!fold.collapsed)}
      />
    </Box>
  );
}

/** Keep the fold action on the property label axis with a 24px hover target. */
export function SummaryFoldRow({
  icon,
  label,
  expanded,
  onClick,
}: {
  icon: IconName;
  label: string;
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      aria-expanded={expanded}
      direction="row"
      align="center"
      alignSelf="start"
      gap="sm"
      height={24}
      paddingX="sm"
      marginXNegative="sm"
      rounded="md"
      hoverBackground
      cursor="pointer"
      onClick={onClick}
    >
      <Box direction="row" align="center" justify="center" width={16} height={16} shrink={false}>
        <Icon name={icon} size="xs" color="muted" />
      </Box>
      <Text size="sm" weight="normal" variant="muted">
        {label}
      </Text>
    </Box>
  );
}
